import axios from "axios";
import jwtDecode from "jwt-decode";
import router from "@/router/index.js";

//export const BASE_URL = "http://localhost:8082/api/";
export const BASE_URL = "https://hospital.shuleone.co.ke/api/";

const apiClientMultipart = axios.create({
  baseURL: BASE_URL,
  processData: false,
  contentType: false,
  cache: false,
  headers: {
    "Content-Type": "multipart/form-data",
  },
});

const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

const tokenRefreshClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Mock triage data (in-memory)
const mockTriageData = {
  "1": [
    {
      id: "triage_1",
      patientId: "1",
      height: 170,
      weight: 65,
      bloodPressure: "120/80",
      pulseRate: 72,
      temperature: 36.6,
      notes: "Patient appears healthy.",
      recordedDate: "2025-04-15",
    },
    {
      id: "triage_2",
      patientId: "1",
      height: 170,
      weight: 66,
      bloodPressure: "118/78",
      pulseRate: 70,
      temperature: 36.7,
      notes: "Slight weight increase noted.",
      recordedDate: "2025-04-20",
    },
  ],
  "2": [
    {
      id: "triage_3",
      patientId: "2",
      height: 165,
      weight: 55,
      bloodPressure: "110/70",
      pulseRate: 68,
      temperature: 36.5,
      notes: "No complaints reported.",
      recordedDate: "2025-04-18",
    },
  ],
};

// Triage API functions using mock data
export const addTriage = async (patientId, triageData) => {
  try {
    // Simulate adding triage data
    const triageRecord = {
      id: `triage_${Date.now()}`, // Generate a unique ID
      patientId,
      ...triageData,
      recordedDate: triageData.recordedDate || new Date().toISOString().split("T")[0],
    };

    // Add to mock data
    if (!mockTriageData[patientId]) {
      mockTriageData[patientId] = [];
    }
    mockTriageData[patientId].push(triageRecord);

    return { data: triageRecord };
  } catch (error) {
    throw new Error(`Failed to add triage data for patient ID ${patientId}: ${error.message}`);
  }
};

export const getLatestTriageByPatientId = async (patientId) => {
  try {
    const patientTriage = mockTriageData[patientId] || [];
    if (patientTriage.length === 0) {
      return { data: null };
    }
    // Sort by recordedDate descending to get the latest
    const latestTriage = patientTriage.sort((a, b) =>
      new Date(b.recordedDate) - new Date(a.recordedDate)
    )[0];
    return { data: latestTriage };
  } catch (error) {
    throw new Error(`Failed to fetch latest triage for patient ID ${patientId}: ${error.message}`);
  }
};

// Existing API functions (unchanged)
export const getDepartments = async () => {
  try {
    const response = await apiClient.get("departments/");
    return response;
  } catch (error) {
    const status = error.response?.status;
    let message = "Failed to fetch departments";
    if (status === 404) {
      message = "No departments found";
    } else if (status === 500) {
      message = "Server error while fetching departments.";
    } else if (status === 401) {
      message = "Unauthorized access. Please check your authentication token.";
    } else {
      message += `: ${error.message}`;
    }
    throw new Error(message);
  }
};
export const addServiceType = async (categoryData) => {
	try {
		const response = await apiClient.post(`mdservice/types/`, categoryData);
		return response;
	} catch (error) {
		const status = error.response?.status;
		let message = "Failed to add service type";
		if (status === 400) {
			message = "Invalid service type data provided.";
		} else if (status === 500) {
			message = "Server error while adding service type.";
		} else if (status === 401) {
			message =
				"Unauthorized access. Please check your authentication token.";
		} else {
			message += `: ${error.message}`;
		}
		throw new Error(message);
	}
};
export const getServicesByTypeName = async (typeName) => {
	try {
		const response = await apiClient.get(
			`services/name/${encodeURIComponent(typeName)}`
		);
		return response;
	} catch (error) {
		const status = error.response?.status;
		let message = `Failed to fetch services for type ${typeName}`;
		if (status === 404) {
			message = `No services found for type ${typeName}`;
		} else if (status === 500) {
			message = `Server error while fetching services.`;
		} else if (status === 401) {
			message = `Unauthorized access. Please check your authentication token.`;
		} else {
			message += `: ${error.message}`;
		}
		throw new Error(message);
	}
};
// Add this new function for DELETE
export const deleteServiceType = async (typeId) => {
  try {
    const response = await apiClient.delete(`mdservice/types/${typeId}`);
    return response;
  } catch (error) {
    const status = error.response?.status;
    let message = `Failed to delete service type with ID ${typeId}`;
    if (status === 404) {
      message = `Service type with ID ${typeId} not found`;
    } else if (status === 500) {
      message = `Server error while deleting service type.`;
    } else if (status === 401) {
      message = `Unauthorized access. Please check your authentication token.`;
    } else {
      message += `: ${error.message}`;
    }
    throw new Error(message);
  }
};
export const toggleMedicalService = async (serviceId, isActive) => {
	try {
		const endpoint = isActive
			? `services/disable/${serviceId}`
			: `services/enable/${serviceId}`;
		const response = await apiClient.put(endpoint);
		return response;
	} catch (error) {
		const status = error.response?.status;
		let message = `Failed to toggle medical service with ID ${serviceId}`;
		if (status === 404) {
			message = `Medical service with ID ${serviceId} not found`;
		} else if (status === 500) {
			message = "Server error while toggling medical service.";
		} else if (status === 401) {
			message =
				"Unauthorized access. Please check your authentication token.";
		} else {
			message += `: ${error.message}`;
		}
		throw new Error(message);
	}
};

export const getStaffById = async (staffId) => {
  try {
    const response = await apiClient.get(`staffs/${staffId}`);
    return response;
  } catch (error) {
    const status = error.response?.status;
    let message = `Failed to fetch staff details for ID ${staffId}`;
    if (status === 404) {
      message = `Staff with ID ${staffId} not found`;
    } else if (status === 500) {
      message = `Server error while fetching staff details.`;
    } else if (status === 401) {
      message = `Unauthorized access. Please check your authentication token.`;
    } else {
      message += `: ${error.message}`;
    }
    throw new Error(message);
  }
};

export const getConsultationsByStaffId = async (staffId) => {
  try {
    const response = await apiClient.get(
      `consultations/staff/${staffId}?active=true`
    );
    return response;
  } catch (error) {
    const status = error.response?.status;
    let message = `Failed to fetch consultations for staff ID ${staffId}`;
    if (status === 404) {
      message = `No active consultations found for staff ID ${staffId}`;
    } else if (status === 500) {
      message = `Server error while fetching consultations.`;
    } else if (status === 401) {
      message = `Unauthorized access. Please check your authentication token.`;
    } else {
      message += `: ${error.message}`;
    }
    throw new Error(message);
  }
};

export const assignStaffToConsultation = async (consultationId, staffId) => {
  try {
    const response = await apiClient.put(
      `consultations/${consultationId}/${staffId}`
    );
    return response;
  } catch (error) {
    const status = error.response?.status;
    let message = `Failed to assign staff to consultation ID ${consultationId}`;
    if (status === 404) {
      message = `Consultation ID ${consultationId} or Staff ID ${staffId} not found`;
    } else if (status === 500) {
      message = `Server error while assigning staff`;
    } else if (status === 401) {
      message = `Unauthorized access. Please check your authentication token`;
    } else {
      message += `: ${error.message}`;
    }
    throw new Error(message);
  }
};

export const getStaffByDepartment = async (departmentId) => {
  try {
    const response = await apiClient.get(`staffs/dep/${departmentId}`);
    return response;
  } catch (error) {
    const status = error.response?.status;
    let message = `Failed to fetch staff for department ID ${departmentId}`;
    if (status === 404) {
      message = `No staff found for department ID ${departmentId}`;
    } else if (status === 500) {
      message = `Server error while fetching staff data.`;
    } else if (status === 401) {
      message = `Unauthorized access. Please check your authentication token.`;
    } else {
      message += `: ${error.message}`;
    }
    throw new Error(message);
  }
};

export const getPatients = async () => {
  try {
    const response = await apiClient.get("patients/");
    return response;
  } catch (error) {
    const status = error.response?.status;
    let message = "Failed to fetch patients";
    if (status === 404) {
      message = "No patients found";
    } else if (status === 500) {
      message = "Server error while fetching patients.";
    } else if (status === 401) {
      message = "Unauthorized access. Please check your authentication token.";
    } else {
      message += `: ${error.message}`;
    }
    throw new Error(message);
  }
};

export const getPatientById = async (patientId) => {
  try {
    const response = await apiClient.get(`patients/${patientId}`);
    return response;
  } catch (error) {
    const status = error.response?.status;
    let message = `Failed to fetch patient details for ID ${patientId}`;
    if (status === 404) {
      message = `Patient with ID ${patientId} not found`;
    } else if (status === 500) {
      message = `Server error while fetching patient details for ID ${patientId}.`;
    } else if (status === 401) {
      message = `Unauthorized access. Please check your authentication token.`;
    } else {
      message += `: ${error.message}`;
    }
    throw new Error(message);
  }
};

export const addPatient = async (patientData) => {
  try {
    const response = await apiClient.post("patients/", {
      ...patientData,
      // Explicitly exclude triage-related fields
      height: undefined,
      weight: undefined,
      bloodPressure: undefined,
    });
    return response;
  } catch (error) {
    const status = error.response?.status;
    let message = "Failed to add patient";
    if (status === 400) {
      message = "Invalid patient data provided.";
    } else if (status === 500) {
      message = "Server error while adding patient.";
    } else if (status === 401) {
      message = "Unauthorized access. Please check your authentication token.";
    } else {
      message += `: ${error.message}`;
    }
    throw new Error(message);
  }
};

export const updatePatient = async (patientId, patientData) => {
  try {
    const response = await apiClient.put(`patients/${patientId}`, {
      ...patientData,
      // Explicitly exclude triage-related fields
      height: undefined,
      weight: undefined,
      bloodPressure: undefined,
    });
    return response;
  } catch (error) {
    const status = error.response?.status;
    let message = `Failed to update patient with ID ${patientId}`;
    if (status === 404) {
      message = `Patient with ID ${patientId} not found`;
    } else if (status === 400) {
      message = "Invalid patient data provided.";
    } else if (status === 500) {
      message = "Server error while updating patient.";
    } else if (status === 401) {
      message = "Unauthorized access. Please check your authentication token.";
    } else {
      message += `: ${error.message}`;
    }
    throw new Error(message);
  }
};

export const getConsultationsByPatientId = async (patientId) => {
  try {
    const response = await apiClient.get(
      `consultations/patient/${patientId}?active=true`
    );
    return response;
  } catch (error) {
    const status = error.response?.status;
    let message = `Failed to fetch consultations for patient ID ${patientId}`;
    if (status === 404) {
      message = `No consultations found for patient ID ${patientId}`;
    } else if (status === 500) {
      message = `Server error while fetching consultations.`;
    } else if (status === 401) {
      message = `Unauthorized access. Please check your authentication token.`;
    } else {
      message += `: ${error.message}`;
    }
    throw new Error(message);
  }
}

export const getTriageByConsultationId = async (consultationId) => {
	try {
		const response = await apiClient.get(`consultations/triage/${consultationId}`);
    console.log(response)

		return response;
	} catch (error) {
		const status = error.response?.status;
		let message = `Failed to fetch consultation triage for consultation ID ${consultationId}`;
		if (status === 404) {
			message = `No triage information found for consultations ${consultationId}`;
		} else if (status === 500) {
			message = `Server error while fetching triage.`;
		} else if (status === 401) {
			message = `Unauthorized access. Please check your authentication token.`;
		} else {
			message += `: ${error.message}`;
		}
		throw new Error(message);
	}
};





export const addConsultation = async (patientId, consultationData) => {
  try {
    const response = await apiClient.post(
      `consultations/${patientId}`,
      consultationData
    );
    return response;
  } catch (error) {
    const status = error.response?.status;
    let message = `Failed to add consultation for patient ID ${patientId}`;
    if (status === 400) {
      message = `Invalid consultation data provided.`;
    } else if (status === 500) {
      message = `Server error while adding consultation.`;
    } else if (status === 401) {
      message = `Unauthorized access. Please check your authentication token.`;
    } else {
      message += `: ${error.message}`;
    }
    throw new Error(message);
  }
};

export const checkStaffAvailability = async () => {
  try {
    const response = await apiClient.get(
      `staffs/status?status=activeavailable`
    );
    return response;
  } catch (error) {
    const status = error.response?.status;
    let message = `Failed to check staff availability`;
    if (status === 404) {
      message = `No active staff found`;
    } else if (status === 500) {
      message = `Server error while checking staff availability.`;
    } else if (status === 401) {
      message = `Unauthorized access. Please check your authentication token.`;
    } else {
      message += `: ${error.message}`;
    }
    throw new Error(message);
  }
};

export const getConsultations = async () => {
  try {
    const response = await apiClient.get("consultations/");
    return response;
  } catch (error) {
    const status = error.response?.status;
    let message = "Failed to fetch consultations";
    if (status === 404) {
      message = "No consultations found";
    } else if (status === 500) {
      message = "Server error while fetching consultations.";
    } else if (status === 401) {
      message = "Unauthorized access. Please check your authentication token.";
    } else {
      message += `: ${error.message}`;
    }
    throw new Error(message);
  }
};

export const getPatientDiagnosis = async (patient_id) => {
  try {
    const response = await apiClient.get(`diagnoses/${patient_id}`);
    return response;
  } catch (error) {
    const status = error.response?.status;
    let message = `Failed to fetch diagnosis for patient ID ${patient_id}`;
    if (status === 404) {
      message = `No diagnosis found for patient ID ${patient_id}`;
    } else if (status === 500) {
      message = `Server error while fetching diagnosis.`;
    } else if (status === 401) {
      message = `Unauthorized access. Please check your authentication token.`;
    } else {
      message += `: ${error.message}`;
    }
    throw new Error(message);
  }
};

export const getServiceById = async (patient_id) => {
  try {
    if (!patient_id) {
      throw new Error("Patient ID is required");
    }
    const response = await apiClient.get(`consultations/${patient_id}`);
    return response;
  } catch (error) {
    const status = error.response?.status;
    let message = `Failed to fetch patient details for ID ${patient_id}`;
    if (status === 404) {
      message = `Patient with ID ${patient_id} not found`;
    } else if (status === 500) {
      message = `Server error while fetching patient details for ID ${patient_id}.`;
    } else if (status === 401) {
      message = `Unauthorized access. Please check your authentication token.`;
    } else {
      message += `: ${error.message}`;
    }
    throw new Error(message);
  }
};

export const getWards = async () => {
  try {
    const response = await apiClient.get("wards/beds");
    return response;
  } catch (error) {
    const status = error.response?.status;
    let message = "Failed to fetch wards";
    if (status === 404) {
      message = "No wards found";
    } else if (status === 500) {
      message = "Server error while fetching wards.";
    } else if (status === 401) {
      message = "Unauthorized access. Please check your authentication token.";
    } else {
      message += `: ${error.message}`;
    }
    throw new Error(message);
  }
};

export const getBedsByWard = async (wardId) => {
  try {
    const response = await apiClient.get(`beds/${wardId}/ward`);
    return response;
  } catch (error) {
    const status = error.response?.status;
    let message = `Failed to fetch beds for ward ID ${wardId}`;
    if (status === 404) {
      message = `No beds found for ward ID ${wardId}`;
    } else if (status === 500) {
      message = `Server error while fetching beds.`;
    } else if (status === 401) {
      message = "Unauthorized access. Please check your authentication token.";
    } else {
      message += `: ${error.message}`;
    }
    throw new Error(message);
  }
};

export const getServiceAdmissions = async (patient_id) => {
  try {
    const response = await apiClient.get(
      `admissions/${patient_id}/?command=service`
    );
    return response;
  } catch (error) {
    const status = error.response?.status;
    let message = `Failed to fetch admissions for patient ID ${patient_id}`;
    if (status === 404) {
      message = `No admissions found for patient ID ${patient_id}`;
    } else if (status === 500) {
      message = `Server error while fetching admissions.`;
    } else if (status === 401) {
      message = `Unauthorized access. Please check your authentication token.`;
    } else {
      message += `: ${error.message}`;
    }
    throw new Error(message);
  }
};

export const getConsultationLaboratoryReports = async (patient_id) => {
  try {
    const response = await apiClient.get(`consultations/${patient_id}/files`);
    return response;
  } catch (error) {
    const status = error.response?.status;
    let message = `Failed to fetch laboratory reports for patient ID ${patient_id}`;
    if (status === 404) {
      message = `No laboratory reports found for patient ID ${patient_id}`;
    } else if (status === 500) {
      message = `Server error while fetching laboratory reports.`;
    } else if (status === 401) {
      message = `Unauthorized access. Please check your authentication token.`;
    } else {
      message += `: ${error.message}`;
    }
    throw new Error(message);
  }
};

export const getServiceChargesAndCosts = async (patientId) => {
  try {
    const response = await apiClient.get(`transactions/${patientId}/consultation/list`);
    return response;
  } catch (error) {
    const status = error.response?.status;
    let message = `Failed to fetch service charges for patient ID ${patientId}`;
    if (status === 404) {
      message = `No service charges found for patient ID ${patientId}`;
    } else if (status === 500) {
      message = `Server error while fetching service charges.`;
    } else if (status === 401) {
      message = `Unauthorized access. Please check your authentication token.`;
    } else {
      message += `: ${error.message}`;
    }
    throw new Error(message);
  }
};

export const endThisService = async (patient_id) => {
  try {
    const response = await apiClient.put(`consultations/${patient_id}`);
    return response;
  } catch (error) {
    const status = error.response?.status;
    let message = `Failed to end service for patient ID ${patient_id}`;
    if (status === 404) {
      message = `Service not found for patient ID ${patient_id}`;
    } else if (status === 500) {
      message = `Server error while ending service.`;
    } else if (status === 401) {
      message = `Unauthorized access. Please check your authentication token.`;
    } else {
      message += `: ${error.message}`;
    }
    throw new Error(message);
  }
};

export const getPatientSymptoms = async (patientId) => {
  try {
    const response = await apiClient.get(`symptoms/${patientId}`);
    return response;
  } catch (error) {
    const status = error.response?.status;
    let message = `Failed to fetch symptoms for patient ID ${patientId}`;
    if (status === 404) {
      message = `No symptoms found for patient ID ${patientId}`;
    } else if (status === 500) {
      message = `Server error while fetching symptoms.`;
    } else if (status === 401) {
      message = `Unauthorized access. Please check your authentication token.`;
    } else {
      message += `: ${error.message}`;
    }
    throw new Error(message);
  }
};

export const savePatientSymptom = async (patientId, symptomData = {}) => {
  try {
    const response = await apiClient.post(
      `symptoms/${patientId}`,
      symptomData
    );
    return response;
  } catch (error) {
    const status = error.response?.status;
    let message = `Failed to save symptom for patient ID ${patientId}`;
    if (status === 400) {
      message = `Invalid symptom data provided.`;
    } else if (status === 500) {
      message = `Server error while saving symptom.`;
    } else if (status === 401) {
      message = `Unauthorized access. Please check your authentication token.`;
    } else {
      message += `: ${error.message}`;
    }
    throw new Error(message);
  }
};

export const getMedicalServices = async () => {
  try {
    const response = await apiClient.get("services/");
    return response;
  } catch (error) {
    const status = error.response?.status;
    let message = "Failed to fetch medical services";
    if (status === 404) {
      message = "No medical services found";
    } else if (status === 500) {
      message = "Server error while fetching medical services.";
    } else if (status === 401) {
      message = "Unauthorized access. Please check your authentication token.";
    } else {
      message += `: ${error.message}`;
    }
    throw new Error(message);
  }
};

export const getServiceTypes = async () => {
  try {
    const response = await apiClient.get("mdservice/types/");
    return response;
  } catch (error) {
    const status = error.response?.status;
    let message = "Failed to fetch service types";
    if (status === 404) {
      message = "No service types found";
    } else if (status === 500) {
      message = "Server error while fetching service types.";
    } else if (status === 401) {
      message = "Unauthorized access. Please check your authentication token.";
    } else {
      message += `: ${error.message}`;
    }
    throw new Error(message);
  }
};

export const getMeasureUnits = async () => {
  try {
    const response = await apiClient.get("pharmacy/measures/");
    return response;
  } catch (error) {
    const status = error.response?.status;
    let message = "Failed to fetch measure units";
    if (status === 404) {
      message = "No measure units found";
    } else if (status === 500) {
      message = "Server error while fetching measure units.";
    } else if (status === 401) {
      message = "Unauthorized access. Please check your authentication token.";
    } else {
      message += `: ${error.message}`;
    }
    throw new Error(message);
  }
};

export const addMedicalService = async (serviceData) => {
  try {
    const response = await apiClient.post("services/", serviceData);
    return response;
  } catch (error) {
    const status = error.response?.status;
    let message = "Failed to add medical service";
    if (status === 400) {
      message = "Invalid service data provided.";
    } else if (status === 500) {
      message = "Server error while adding medical service.";
    } else if (status === 401) {
      message = "Unauthorized access. Please check your authentication token.";
    } else {
      message += `: ${error.message}`;
    }
    throw new Error(message);
  }
};

export const savePatientDiagnosis = async (diagnosisData) => {
	try {
		const { patientId, date, symptoms } = diagnosisData;
		if (!patientId || !date || !symptoms) {
			throw new Error("Patient ID, date, and symptoms are required");
		}
		const response = await apiClient.post(`diagnoses/${patientId}`, {
			date,
			symptoms,
		});
		return response;
	} catch (error) {
		const status = error.response?.status;
		let message = `Failed to save diagnosis for patient ID ${diagnosisData.patientId}`;
		if (status === 400) {
			message = "Invalid diagnosis data provided.";
		} else if (status === 404) {
			message = `Patient with ID ${diagnosisData.patientId} not found`;
		} else if (status === 500) {
			message = "Server error while saving diagnosis.";
		} else if (status === 401) {
			message =
				"Unauthorized access. Please check your authentication token.";
		} else {
			message += `: ${error.message}`;
		}
		throw new Error(message);
	}
};

function isAccessTokenExpired() {
  const accessToken = localStorage.getItem("accessToken");
  if (accessToken) {
    const decodedToken = jwtDecode(accessToken);
    const currentTime = Date.now() / 1000;
    return decodedToken.exp < currentTime;
  }
  return true;
}

apiClient.interceptors.request.use(
  async (config) => {
    const accessToken = localStorage.getItem("accessToken");
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    router.push("/").catch(() => {});
    console.log(error);
    return Promise.reject(error);
  }
);

apiClientMultipart.interceptors.request.use(
  async (config) => {
    const accessToken = localStorage.getItem("accessToken");
    const refreshToken = localStorage.getItem("refreshToken");
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    if (config.url !== "auth/refresh" && isAccessTokenExpired()) {
      try {
        const refreshResponse = await tokenRefreshClient.post(
          "auth/refresh",
          { refreshToken }
        );
        const newAccessToken = refreshResponse.data.accessToken;
        localStorage.setItem("accessToken", newAccessToken);
        config.headers.Authorization = `Bearer ${newAccessToken}`;
      } catch (error) {
        console.error("Error refreshing token:", error);
        router.push("/");
      }
    }
    return config;
  },
  (error) => {
    router.push("/").catch(() => {});
    console.log(error);
    return Promise.reject(error);
  }
);

export default {
  async login(username, password) {
    try {
      const response = await apiClient.post("/auth/signin", {
        username,
        password,
      });
      return response.data;
    } catch (error) {
      throw new Error(`Login failed: ${error.message}`);
    }
  },
  logout() {
    localStorage.clear();
    router.push("/");
  },
  async getImage(imageName, type) {
    try {
      const response = await apiClient.get(`images/getimage/${imageName}/${type}`, { responseType: "blob" });
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch image: ${error.message}`);
    }
  },
  async fetchindexpagedata() {
    try {
      const response = await apiClient.get("index/getindexpagedata");
      return response.data[0];
    } catch (error) {
      throw new Error(`Failed to fetch index page data: ${error.message}`);
    }
  },
  async getclasseswithall() {
    try {
      const response = await apiClient.get("index/getclsseswithall");
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch classes: ${error.message}`);
    }
  },
  async getactiveclassesandallumni() {
    try {
      const response = await apiClient.get("index/getactiveclassesandallumni");
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch active classes and alumni: ${error.message}`);
    }
  },
  async getstreams() {
    try {
      const response = await apiClient.get("index/getstreams");
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch streams: ${error.message}`);
    }
  },
  async getcounty() {
    try {
      const response = await apiClient.get("index/getcounty");
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch counties: ${error.message}`);
    }
  },
  async getsubcounty() {
    try {
      const response = await apiClient.get("index/getsubcounty");
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch sub-counties: ${error.message}`);
    }
  },
  async gethostels() {
    try {
      const response = await apiClient.get("index/gethostels");
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch hostels: ${error.message}`);
    }
  },
  async getAllStudents() {
    try {
      const response = await apiClient.get("reg/getActiveStudents");
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch students: ${error.message}`);
    }
  },
  async getpopulationfigures() {
    try {
      const response = await apiClient.get("index/getpopulationfigures");
      return response.data[0];
    } catch (error) {
      throw new Error(`Failed to fetch population figures: ${error.message}`);
    }
  },
  async getpopulationperclass() {
    try {
      const response = await apiClient.get("index/getpopulationperclass");
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch population per class: ${error.message}`);
    }
  },
  async getUserPrivileges() {
    try {
      const response = await apiClient.get("index/getuserpriveleges");
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch user privileges: ${error.message}`);
    }
  },
  async uploadFileWithProgress(file, onUploadProgress, uploadUrl, otherdata) {
    try {
      let formData = new FormData();
      formData.append("file", file);
      formData.append("otherdata", otherdata);
      const response = await apiClientMultipart.post(uploadUrl, formData, {
        onUploadProgress,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response;
    } catch (error) {
      throw new Error(`Failed to upload file: ${error.message}`);
    }
  },
  async getDataDynamic(path) {
    try {
      const response = await apiClient.get(path);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch data from ${path}: ${error.message}`);
    }
  },
  apiClient,
  apiClientMultipart,
};